import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { radioStations, type RadioStation } from '@/mocks/radios';
import type { TechnicalStreamStatus } from '@/types/streaming';
import { canPlayStream, shouldFallbackToRfw, isTerminalStatus, isTransientStatus, technicalStreamStatusMessages } from '@/types/streaming';

/* ── Bandera de demo ── */
const DEMO_ENABLED = import.meta.env.VITE_ENABLE_DEMO_DATA === 'true';

/* ── Reconexión ── */
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 2000;

// ── Tipos ──

export type PlayerStatus =
  | 'idle'
  | 'loading'
  | 'playing'
  | 'buffering'
  | 'error'
  | 'offline';

export const playerStatusMessage: Record<PlayerStatus, string> = {
  idle: 'Toca reproducir para escuchar',
  loading: 'Cargando audio...',
  playing: '',
  buffering: 'Conectando...',
  error: 'Stream no disponible',
  offline: 'Radio fuera de línea',
};

export interface FallbackInfo {
  originalStationName: string;
  originalFrequency: string;
}

export interface StreamErrorInfo {
  stationName: string;
  stationFrequency: string;
  technicalStatus: TechnicalStreamStatus;
  message: string;
}

interface AudioContextType {
  currentStation: RadioStation | null;
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  playerStatus: PlayerStatus;
  fallbackInfo: FallbackInfo | null;
  streamError: StreamErrorInfo | null;
  audioError: string | null;
  play: (station: RadioStation) => void;
  pause: () => void;
  toggle: () => void;
  stop: () => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  changeStation: (station: RadioStation) => void;
  dismissFallback: () => void;
  dismissStreamError: () => void;
  stations: RadioStation[];
}

const AudioContext = createContext<AudioContextType | null>(null);

/**
 * Obtiene la radio de plataforma RFW desde mocks SOLO en demo.
 * En producción, la radio de plataforma se recibe desde DigitalDial
 * con el stream URL real de CentovaCast.
 */
function getMockRfwRadio(): RadioStation | undefined {
  if (!DEMO_ENABLED) return undefined;
  return radioStations.find((s) => s.id === 0);
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const [currentStation, setCurrentStation] = useState<RadioStation | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(80);
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>('idle');
  const [fallbackInfo, setFallbackInfo] = useState<FallbackInfo | null>(null);
  const [streamError, setStreamError] = useState<StreamErrorInfo | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectActiveRef = useRef(false);
  const lastStationRef = useRef<RadioStation | null>(null);
  const savedVolumeRef = useRef(80);

  const dismissFallback = useCallback(() => {
    setFallbackInfo(null);
  }, []);

  const dismissStreamError = useCallback(() => {
    setStreamError(null);
  }, []);

  // ── Cancelar reconexión ──
  const cancelReconnect = useCallback(() => {
    reconnectActiveRef.current = false;
    reconnectAttemptsRef.current = 0;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // ── Helper: detener audio actual ──
  const stopAudio = useCallback(() => {
    cancelReconnect();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();
      audioRef.current.onerror = null;
      audioRef.current.oncanplay = null;
      audioRef.current.onplaying = null;
      audioRef.current.onwaiting = null;
      audioRef.current.onended = null;
      audioRef.current = null;
    }
    setAudioError(null);
    setPlayerStatus('idle');
  }, [cancelReconnect]);

  // ── Reconexión controlada ──
  const scheduleReconnect = useCallback((station: RadioStation) => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      reconnectActiveRef.current = false;
      setPlayerStatus('error');
      setAudioError('Stream no disponible');
      return;
    }

    reconnectActiveRef.current = true;
    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current);
    reconnectAttemptsRef.current += 1;

    reconnectTimerRef.current = setTimeout(() => {
      if (!reconnectActiveRef.current) return;
      setPlayerStatus('loading');

      const url = station.streamUrl || station.publicStreamUrl || '';
      if (!url) {
        setPlayerStatus('error');
        setAudioError('URL del stream no disponible');
        return;
      }

      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.src = url;
      audio.volume = isMuted ? 0 : volume / 100;

      audio.oncanplay = () => {
        if (!reconnectActiveRef.current) return;
        reconnectAttemptsRef.current = 0;
        setPlayerStatus('playing');
        setAudioError(null);
      };

      audio.onwaiting = () => {
        if (!reconnectActiveRef.current) return;
        setPlayerStatus('buffering');
      };

      audio.onplaying = () => {
        if (!reconnectActiveRef.current) return;
        setPlayerStatus('playing');
      };

      audio.onerror = () => {
        if (!reconnectActiveRef.current) return;
        console.warn('[AudioContext] Reconnect error for station:', station.name, 'attempt:', reconnectAttemptsRef.current);
        audio.src = '';
        audio.load();
        scheduleReconnect(station);
      };

      // Limpiar listeners del audio anterior
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load();
        audioRef.current.onerror = null;
        audioRef.current.oncanplay = null;
        audioRef.current.onplaying = null;
        audioRef.current.onwaiting = null;
        audioRef.current.onended = null;
      }

      audioRef.current = audio;

      audio.play().catch(() => {
        if (!reconnectActiveRef.current) return;
        scheduleReconnect(station);
      });
    }, delay);
  }, [volume, isMuted]);

  // ── Helper: iniciar audio con stream URL ──
  const startAudio = useCallback((station: RadioStation) => {
    stopAudio();
    cancelReconnect();

    const url = station.streamUrl || station.publicStreamUrl || '';
    if (!url) {
      setPlayerStatus('error');
      setAudioError('Esta radio no tiene un stream URL configurado.');
      return;
    }

    setPlayerStatus('loading');
    lastStationRef.current = station;

    const audio = new Audio();
    audio.crossOrigin = 'anonymous';
    audio.src = url;
    audio.volume = isMuted ? 0 : volume / 100;

    audio.oncanplay = () => {
      setPlayerStatus('playing');
      setAudioError(null);
      reconnectAttemptsRef.current = 0;
    };

    audio.onwaiting = () => {
      setPlayerStatus('buffering');
    };

    audio.onplaying = () => {
      setPlayerStatus('playing');
    };

    audio.onerror = () => {
      console.warn('[AudioContext] Audio error for station:', station.name, 'URL:', url);
      if (!reconnectActiveRef.current && lastStationRef.current?.id === station.id) {
        setPlayerStatus('loading');
        scheduleReconnect(station);
      }
    };

    audio.onended = () => {
      // Streams SHOUTcast/Icecast no terminan normalmente,
      // si llega onended es porque hubo desconexión
      if (!reconnectActiveRef.current && lastStationRef.current?.id === station.id) {
        scheduleReconnect(station);
      }
    };

    audioRef.current = audio;

    audio.play().catch((err) => {
      console.warn('[AudioContext] Play error:', err);
      // El navegador puede rechazar autoplay sin interacción del usuario
      if (err.name === 'NotAllowedError') {
        setPlayerStatus('idle');
        setAudioError('Toca reproducir para escuchar');
        setIsPlaying(false);
        return;
      }
      setPlayerStatus('error');
      setAudioError(`No se pudo iniciar la reproducción. Verificá tu conexión.`);
    });
  }, [stopAudio, cancelReconnect, volume, isMuted, scheduleReconnect]);

  const play = useCallback((station: RadioStation) => {
    const techStatus = station.technicalStreamStatus;

    // ── CASO 1: Stream activo — reproducción normal ──
    if (canPlayStream(techStatus)) {
      setFallbackInfo(null);
      setStreamError(null);

      if (currentStation && currentStation.id === station.id) {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
          setPlayerStatus('playing');
        } else {
          startAudio(station);
        }
        setIsPlaying(true);
        return;
      }

      // Cambio de estación: detener anterior y cargar nueva
      stopAudio();
      cancelReconnect();
      setCurrentStation(station);
      setIsPlaying(true);
      startAudio(station);
      return;
    }

    // ── CASO 2: Fallback a RFW Radio (solo demo, usando mocks) ──
    if (shouldFallbackToRfw(techStatus)) {
      const rfwRadio = getMockRfwRadio();
      if (!rfwRadio) {
        // Sin mock en producción: reproducir la estación tal cual
        setCurrentStation(station);
        setIsPlaying(true);
        startAudio(station);
        return;
      }

      if (currentStation?.id === 0 && fallbackInfo?.originalStationName === station.name) {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
          setPlayerStatus('playing');
        } else {
          startAudio(rfwRadio);
        }
        setIsPlaying(true);
        return;
      }

      stopAudio();
      cancelReconnect();
      setCurrentStation(rfwRadio);
      setIsPlaying(true);
      setStreamError(null);
      setFallbackInfo({
        originalStationName: station.name || 'Esta radio',
        originalFrequency: station.frequency || '',
      });
      startAudio(rfwRadio);
      return;
    }

    // ── CASO 3: Estado terminal o transitorio — no se puede reproducir ──
    setFallbackInfo(null);
    stopAudio();
    setCurrentStation(null);
    setIsPlaying(false);
    setStreamError({
      stationName: station.name || 'Esta radio',
      stationFrequency: station.frequency || '',
      technicalStatus: techStatus,
      message: technicalStreamStatusMessages[techStatus] || 'No se puede reproducir esta radio en este momento.',
    });
  }, [currentStation, fallbackInfo, startAudio, stopAudio, cancelReconnect]);

  const pause = useCallback(() => {
    // Cancelar cualquier reconexión en curso
    cancelReconnect();
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setPlayerStatus('idle');
  }, [cancelReconnect]);

  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else if (currentStation) {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {});
        setPlayerStatus('playing');
      } else {
        startAudio(currentStation);
      }
      setIsPlaying(true);
    }
  }, [isPlaying, currentStation, pause, startAudio]);

  const stop = useCallback(() => {
    stopAudio();
    setIsPlaying(false);
    setCurrentStation(null);
    setFallbackInfo(null);
    setStreamError(null);
    setAudioError(null);
    setPlayerStatus('idle');
    lastStationRef.current = null;
  }, [stopAudio]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      if (audioRef.current) {
        if (newMuted) {
          savedVolumeRef.current = volume;
          audioRef.current.volume = 0;
        } else {
          audioRef.current.volume = savedVolumeRef.current / 100;
        }
      }
      return newMuted;
    });
  }, [volume]);

  const changeStation = useCallback((station: RadioStation) => {
    const techStatus = station.technicalStreamStatus;

    // Detener audio anterior y cancelar reconexión
    stopAudio();
    cancelReconnect();

    // ── CASO 1: Stream activo ──
    if (canPlayStream(techStatus)) {
      setFallbackInfo(null);
      setStreamError(null);
      setCurrentStation(station);
      setIsPlaying(true);
      startAudio(station);
      return;
    }

    // ── CASO 2: Fallback a RFW Radio (solo demo) ──
    if (shouldFallbackToRfw(techStatus)) {
      const rfwRadio = getMockRfwRadio();
      if (!rfwRadio) {
        setCurrentStation(station);
        setIsPlaying(true);
        startAudio(station);
        return;
      }

      if (currentStation?.id === 0 && fallbackInfo?.originalStationName === station.name) {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
          setPlayerStatus('playing');
        } else {
          startAudio(rfwRadio);
        }
        setIsPlaying(true);
        return;
      }

      setCurrentStation(rfwRadio);
      setIsPlaying(true);
      setStreamError(null);
      setFallbackInfo({
        originalStationName: station.name || 'Esta radio',
        originalFrequency: station.frequency || '',
      });
      startAudio(rfwRadio);
      return;
    }

    // ── CASO 3: No se puede reproducir ──
    setFallbackInfo(null);
    setCurrentStation(null);
    setIsPlaying(false);
    setStreamError({
      stationName: station.name || 'Esta radio',
      stationFrequency: station.frequency || '',
      technicalStatus: techStatus,
      message: technicalStreamStatusMessages[techStatus] || 'No se puede reproducir esta radio en este momento.',
    });
  }, [currentStation, fallbackInfo, startAudio, stopAudio, cancelReconnect]);

  // ── Volume sync ──
  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    if (!isMuted && audioRef.current) {
      audioRef.current.volume = v / 100;
    }
    savedVolumeRef.current = v;
  }, [isMuted]);

  // Efecto: sincronizar volumen cuando cambia (sin depender de isMuted para no causar re-renders extra)
  useEffect(() => {
    if (audioRef.current && !isMuted) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume, isMuted]);

  // ── RFW Radio: auto-carga para nuevos usuarios (solo demo) ──
  useEffect(() => {
    if (!DEMO_ENABLED) return;
    const hasVisited = localStorage.getItem('rfw_has_visited');
    if (!hasVisited) {
      localStorage.setItem('rfw_has_visited', 'true');
      const rfwRadio = getMockRfwRadio();
      if (rfwRadio) {
        setCurrentStation(rfwRadio);
        setIsPlaying(true);
        startAudio(rfwRadio);
      }
    }
    // Solo ejecutar en mount, no depende de startAudio
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      cancelReconnect();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load();
        audioRef.current.onerror = null;
        audioRef.current.oncanplay = null;
        audioRef.current.onplaying = null;
        audioRef.current.onwaiting = null;
        audioRef.current.onended = null;
        audioRef.current = null;
      }
    };
  }, [cancelReconnect]);

  return (
    <AudioContext.Provider
      value={{
        currentStation,
        isPlaying,
        isMuted,
        volume,
        playerStatus,
        fallbackInfo,
        streamError,
        audioError,
        play,
        pause,
        toggle,
        stop,
        setVolume,
        toggleMute,
        changeStation,
        dismissFallback,
        dismissStreamError,
        stations: radioStations,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioProvider');
  }
  return context;
}